import React, { forwardRef } from 'react';
import { CircularProgress, Box, SxProps, Breakpoint } from '@mui/material';
import MuiButton, { ButtonProps } from '@mui/material/Button';

import { withSxProp } from 'common/utils/props';

const buttonStyle: SxProps = {
    textTransform: 'none',
    fontWeight: 'fontWeightBold',
    lineHeight: 1.2,
    p: '16px 24px',

    '&.MuiButton-outlined': {
        p: '15px 23px',
    },

    '&.MuiButton-text.MuiButton-sizeSmall, &.MuiButton-containedSizeSmall': {
        p: '7px 11px',
    },

    '&.MuiButton-outlined.MuiButton-sizeSmall': {
        p: '6px 10px',
    },

    '&.MuiButton-sizeLarge': {
        fontSize: 18,
    },

    '&.MuiButton-text.MuiButton-sizeLarge, &.MuiButton-containedSizeLarge': {
        p: '24px 32px',
    },

    '&.MuiButton-outlined.MuiButton-sizeLarge': {
        p: '23px 31px',
    },

    '&.MuiLink-underlineHover:hover': {
        textDecoration: 'none',
    },

    '.spinner': {
        display: 'flex',
        alignItems: 'center',
        color: 'action.disabled',

        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
    },

    '.MuiButton-startIcon': {
        mr: 1.5,
        ml: -1,
    },

    '.MuiButton-endIcon': {
        ml: 1.5,
        mr: -1,
    },

    '&.MuiButton-outlined.MuiButton-sizeSmall .MuiButton-startIcon': {
        mr: 0.5,
        ml: -0.5,
    },
};

// Style for buttons with no content
const noContentStyle: SxProps = {
    '&.MuiButton-outlined.MuiButton-sizeSmall': {
        minWidth: 0,

        '.MuiButton-startIcon': {
            mr: -0.5,
            ml: -0.5,
        },
    },
    '&.MuiButton-outlined.MuiButton-sizeMedium': {
        minWidth: 0,

        '.MuiButton-startIcon': {
            mr: -1,
            ml: -1,
        },
    },
};

interface IProps {
    hideTextBelow?: Breakpoint;
    loading?: boolean;
}

function getProgressSize(size: ButtonProps['size']) {
    switch (size) {
        case 'small':
            return 16;
        case 'large':
            return 28;
        case 'medium':
        default:
            return 20;
    }
}

export function BaseButton<C extends React.ElementType>(
    {
        children,
        color,
        disabled,
        hideTextBelow,
        loading,
        size,
        sx,
        ...other
    }: ButtonProps<C, { component?: C } & IProps>,
    ref: React.Ref<HTMLButtonElement>
) {
    const buttonContent = hideTextBelow ? (
        <Box sx={{ display: { xs: 'none', [hideTextBelow]: 'block' } }}>
            {children}
        </Box>
    ) : (
        children
    );

    return (
        <MuiButton
            color={color}
            disabled={disabled || loading}
            ref={ref}
            size={size}
            {...other}
            sx={[
                buttonStyle,

                loading && { '&.Mui-disabled': { color: 'transparent' } },

                !color && {
                    '&.MuiButton-contained': {
                        backgroundColor: 'grey.200',
                        color: 'grey.900',

                        '&:hover': {
                            backgroundColor: 'grey.300',
                        },
                    },
                },

                !children && noContentStyle,

                hideTextBelow && {
                    px: { xs: 2, [hideTextBelow]: 3 },
                    py: { xs: 2, [hideTextBelow]: 2 },
                    minWidth: { xs: 0, [hideTextBelow]: '64px' },
                    '& .MuiButton-startIcon': {
                        mr: { xs: 0, [hideTextBelow]: 1.5 },
                        ml: { xs: 0, [hideTextBelow]: -1 },
                    },
                    '& .MuiButton-endIcon': {
                        ml: { xs: 0, [hideTextBelow]: 1.5 },
                        mr: { xs: 0, [hideTextBelow]: -1 },
                    },
                    '&.MuiButton-outlined': {
                        p: { xs: '15px', [hideTextBelow]: '15px 23px' },
                    },
                    '&.MuiButton-sizeSmall': {
                        p: { xs: '7px', [hideTextBelow]: '7px 11px' },
                        '&.MuiButton-outlined': {
                            p: { xs: '6px', [hideTextBelow]: '6px 10px' },
                        },
                    },
                    '&.MuiButton-sizeLarge': {
                        px: { xs: 3, [hideTextBelow]: 4 },
                        py: { xs: 3, [hideTextBelow]: 2 },
                        '&.MuiButton-outlined': {
                            p: { xs: '23px', [hideTextBelow]: '23px 31px' },
                        },
                    },
                },

                // Spread sx from props
                ...withSxProp(sx),
            ]}
        >
            {buttonContent}
            {loading && (
                <Box className="spinner">
                    <CircularProgress
                        color="inherit"
                        size={getProgressSize(size)}
                    />
                </Box>
            )}
        </MuiButton>
    );
}

export const Button = forwardRef(BaseButton);
